import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useMemo } from "react";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

import DiscussionPageMobileLayout from "./DiscussionPageMobileLayout";
import DiscussionPageTabs from "./DiscussionsPageTabs";
import MostDiscussedWidget from "./MostDiscussedWidget";

const SignUpForm = dynamic(() => import("components/SignUpForm"));

const title = "Discussions | Smartkarma";
const description =
  "Smartkarma is the Independent Investment Research Network, uniting Insight Providers, Investors, and Investor Relations Professionals.";

type DashboardPageLayoutProps = { children: React.ReactNode };

const DiscussionsPageLayout = ({ children }: DashboardPageLayoutProps) => {
  const media = useMediaQuery();

  const router = useRouter();

  const { authenticated } = useAuthentication();

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  const metaTags = useMemo(() => {
    const defaultMetatags = (
      <>
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://assets.smartkarma.com/v3/img/smartkarma-research-reinvented-dark.png"
        />
        <meta name="robots" content="index, follow" />
      </>
    );

    return (
      <>
        {defaultMetatags}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </>
    );
  }, [canonicalUrl]);

  if (media.mobile) {
    return (
      <>
        <Head>{metaTags}</Head>
        <DiscussionPageMobileLayout>{children}</DiscussionPageMobileLayout>
      </>
    );
  }

  return (
    <>
      <Head>{metaTags}</Head>
      <PaneLayout
        className="px-[30px] py-[20px]"
        left={<DiscussionPageTabs />}
        right={
          <div className="mt-[25px]">
            {authenticated ? (
              <>
                <MostDiscussedWidget
                  hideTabs
                  title="Most Discussed Insights"
                  defaultTab="insights"
                />
                <MostDiscussedWidget
                  hideTabs
                  title="Most Discussed Entities"
                  defaultTab="entities"
                />
              </>
            ) : (
              <CustomCard className="p-0">
                <SignUpForm type="compact" />
              </CustomCard>
            )}
          </div>
        }
      >
        <div className="w-full pt-[25px] px-[5px]">
          {authenticated ? (
            children
          ) : (
            <CustomCard className="min-h-[calc(100vh-100px)]">
              <div className="text-center italic">
                <p>
                  Your subscription enables you to engage in discussions with
                  our network of independent analysts, investor relations
                  professionals, and investors.
                </p>
                <div>
                  <a
                    className="text-smart-teal"
                    href={`${emberAppBaseUrl}/home/pricing/`}
                    target="_blank"
                  >
                    See All Plans
                  </a>
                </div>
              </div>
            </CustomCard>
          )}
        </div>
      </PaneLayout>
    </>
  );
};

export default DiscussionsPageLayout;
